import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";

const BETSLIP_KEY = "hw.betslip.v1";

export type BetSlipLeg = {
  key: string;
  raceId: string;
  raceLabel: string;
  scheduledAt: string;
  betType: "win" | "place" | "show" | "exacta" | "quinella" | "trifecta";
  sel: number[];
  selectionLabel: string;
  oddsH: number;
};

type CouponType = "single" | "combined" | "system";

type BetSlipState = {
  open: boolean;
  legs: BetSlipLeg[];
  stakeCents: number;
  couponType: CouponType;
  systemK: number | null;
};

type BetSlipAction =
  | { type: "toggle"; open?: boolean }
  | { type: "add"; leg: BetSlipLeg }
  | { type: "remove"; key: string }
  | { type: "clear" }
  | { type: "stake"; stakeCents: number }
  | { type: "coupon"; couponType: CouponType; systemK?: number | null };

const initialState: BetSlipState = {
  open: false,
  legs: [],
  stakeCents: 500,
  couponType: "single",
  systemK: null,
};

function loadState(): BetSlipState {
  try {
    const saved = JSON.parse(
      window.localStorage.getItem(BETSLIP_KEY) ?? "null",
    ) as Partial<BetSlipState> | null;
    return saved ? { ...initialState, ...saved, open: false } : initialState;
  } catch {
    return initialState;
  }
}

function reducer(state: BetSlipState, action: BetSlipAction): BetSlipState {
  switch (action.type) {
    case "toggle":
      return { ...state, open: action.open ?? !state.open };
    case "add": {
      // The coupon core forbids correlated same-race parlay legs. An exotic
      // is one leg; selecting another market on the same race replaces it.
      const legs = [
        ...state.legs.filter((leg) => leg.raceId !== action.leg.raceId),
        action.leg,
      ].sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
      return {
        ...state,
        open: true,
        legs,
        couponType: legs.length > 1 ? "combined" : "single",
        systemK: null,
      };
    }
    case "remove": {
      const legs = state.legs.filter((leg) => leg.key !== action.key);
      const couponType =
        legs.length <= 1
          ? "single"
          : state.couponType === "system" && legs.length < 3
            ? "combined"
            : state.couponType;
      return {
        ...state,
        legs,
        couponType,
        systemK:
          couponType === "system"
            ? Math.min(state.systemK ?? legs.length - 1, legs.length - 1)
            : null,
      };
    }
    case "clear":
      return { ...initialState, open: state.open };
    case "stake":
      return { ...state, stakeCents: Math.max(0, action.stakeCents) };
    case "coupon":
      return {
        ...state,
        couponType: action.couponType,
        systemK: action.couponType === "system" ? (action.systemK ?? 1) : null,
      };
  }
}

type BetSlipContextValue = BetSlipState & {
  launcher: HTMLElement | null;
  addLeg: (leg: BetSlipLeg, launcher?: HTMLElement | null) => void;
  removeLeg: (key: string) => void;
  clear: () => void;
  setOpen: (open: boolean, launcher?: HTMLElement | null) => void;
  setStakeCents: (stakeCents: number) => void;
  setCouponType: (couponType: CouponType, systemK?: number) => void;
};

const BetSlipContext = createContext<BetSlipContextValue | null>(null);

export function BetSlipProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, loadState);
  const [launcher, setLauncher] = useState<HTMLElement | null>(null);

  useEffect(() => {
    window.localStorage.setItem(
      BETSLIP_KEY,
      JSON.stringify({ ...state, open: undefined }),
    );
  }, [state]);

  const addLeg = useCallback(
    (leg: BetSlipLeg, launcher?: HTMLElement | null) => {
      if (launcher) setLauncher(launcher);
      dispatch({ type: "add", leg });
    },
    [],
  );
  const removeLeg = useCallback(
    (key: string) => dispatch({ type: "remove", key }),
    [],
  );
  const clear = useCallback(() => dispatch({ type: "clear" }), []);
  const setOpen = useCallback(
    (open: boolean, launcher?: HTMLElement | null) => {
      if (open && launcher) setLauncher(launcher);
      dispatch({ type: "toggle", open });
    },
    [],
  );
  const setStakeCents = useCallback(
    (stakeCents: number) => dispatch({ type: "stake", stakeCents }),
    [],
  );
  const setCouponType = useCallback(
    (couponType: CouponType, systemK?: number) =>
      dispatch({ type: "coupon", couponType, systemK }),
    [],
  );

  const value = useMemo(
    () => ({
      ...state,
      launcher,
      addLeg,
      removeLeg,
      clear,
      setOpen,
      setStakeCents,
      setCouponType,
    }),
    [
      state,
      launcher,
      addLeg,
      removeLeg,
      clear,
      setOpen,
      setStakeCents,
      setCouponType,
    ],
  );

  return (
    <BetSlipContext.Provider value={value}>{children}</BetSlipContext.Provider>
  );
}

export function useBetSlip() {
  const value = useContext(BetSlipContext);
  if (!value) throw new Error("useBetSlip must be used inside BetSlipProvider");
  return value;
}
